import { Button, Checkbox, Grid, Group, Loader, Stack, Text } from '@mantine/core';
import { useState } from 'react';

import { User } from '@e-origin/shared';

import { useDeclarants, useRoles } from '@admin/hooks';
import { PasswordInput, Select, TextInput } from '@components';
import { useEditUserFormInitializer } from './useEditUserFormInitializer';

interface EditUserProps {
  user?: User;
  onHide: () => void;
}

export const EditUser: React.FC<EditUserProps> = ({ user, onHide }) => {
  const [formError, setFormError] = useState<string | null>(null);
  const { data: rolesData } = useRoles();
  const { data: declarantsData } = useDeclarants();

  const { form, onSubmit, isSubmitting } = useEditUserFormInitializer({
    user,
  });

  const handleSubmit = async (values) => {
    setFormError(null);

    try {
      await onSubmit(values);
      onHide();
    } catch (error: any) {
      setFormError(error?.response?.data?.message || 'Failed to save user');
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md" my="xl">
        <Group>
          {!!user && (
            <>
              <Checkbox
                {...form.getInputProps('deleted', { type: 'checkbox' })}
                label="Active"
                checked={!form.values.deleted}
                onChange={(event) => form.setFieldValue('deleted', !event.currentTarget.checked)}
              />
              <Checkbox {...form.getInputProps('visible', { type: 'checkbox' })} label="Visible" checked={form.values.visible} />
            </>
          )}
          <Checkbox {...form.getInputProps('isSupervisor', { type: 'checkbox' })} label="Supervisor" checked={form.values.isSupervisor} />
        </Group>

        <TextInput {...form.getInputProps('email')} label="Email" placeholder="Email address" disabled={!!user} />

        <Grid>
          <Grid.Col span={6}>
            <TextInput {...form.getInputProps('firstName')} label="First Name" placeholder="First name" />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput {...form.getInputProps('lastName')} label="Last Name" placeholder="Last name" />
          </Grid.Col>
        </Grid>

        {!user && <PasswordInput {...form.getInputProps('password')} label="Password" withAsterisk type="password" size="lg" />}

        <Select
          label="Role"
          placeholder="Select role"
          data={
            rolesData?.data?.map((role) => ({
              value: role._id,
              label: role.name,
            })) || []
          }
          {...form.getInputProps('role')}
        />

        <Select
          label="Declarant"
          placeholder="Select declarant"
          data={
            declarantsData?.map((declarant) => ({
              value: declarant._id,
              label: declarant.accountName,
            })) || []
          }
          {...form.getInputProps('declarant')}
        />
      </Stack>

      {formError && (
        <Text c="red" fw="bold" mt="md">
          {formError}
        </Text>
      )}

      <Group justify="center" mt="xl">
        <Button type="button" key="cancel-modal-btn" onClick={() => onHide()} disabled={isSubmitting} size="lg" variant="transparent">
          Cancel
        </Button>

        <Button
          key="save-modal-btn"
          disabled={!form.isValid() || isSubmitting}
          type="submit"
          leftSection={isSubmitting && <Loader color="white" />}
          variant="filled"
          size="lg"
        >
          Save
        </Button>
      </Group>
    </form>
  );
};
